import React, { useState, useCallback } from "react";
import { Box, Typography, IconButton, Button, Tooltip, Chip } from "@mui/material";
import { EditOutlined, CloseOutlined } from "@mui/icons-material";
import { TabType, KeyMapItem, KeyConfig, DEFAULT_KEY_CONFIG } from "../../@types/keyboard";
import { KeyMappingModal } from "./KeyMappingModal";
import { useRecoilValue } from "recoil";
import { projectConfigState } from "../../recoil";
import { parseKeyConfig } from "../../utils/keyboardUtils";
import { useKeyMapping } from "../../hooks/useKeyMapping";

const COLORS = {
  labeling: {
    bg: "#eeedfe",
    border: "#afa9ec",
    borderBottom: "#7f77dd",
    text: "#3c3489",
    dot: "#afa9ec",
    hoverBg: "#dedcfc",
  },
  navigation: {
    bg: "#e1f5ee",
    border: "#5dcaa5",
    borderBottom: "#1d9e75",
    text: "#085041",
    dot: "#5dcaa5",
    hoverBg: "#c5eedd",
  },
  action: {
    bg: "#faeeda",
    border: "#ef9f27",
    borderBottom: "#ba7517",
    text: "#412402",
    dot: "#ef9f27",
    hoverBg: "#fce3b0",
  },
  default: {
    bg: "#f8f8fc",
    border: "#d8d8e8",
    borderBottom: "#c8c8da",
    text: "#3a3a5c",
    dot: "#d0d0e0",
    hoverBg: "#f0f0ff",
  },
};

// ─── 타입 ───────────────────────────────────────────────

interface MappedKey {
  label: string; // 키보드에 표시할 문자
  tab?: TabType; // 어느 탭에 매핑되어 있는지
  actionLabel?: string; // 호버 툴팁
  wide?: boolean;
  wider?: boolean;
  isArrowUp?: boolean;
  isArrowDown?: boolean;
}

// ─── 키보드 레이아웃 정의 ────────────────────────────────

function buildLayout(config: KeyConfig): MappedKey[][] {
  const labelKeys = config.labeling.map((i) => i.key);
  const navKeys = config.navigation.map((i) => i.key);
  const actionKeys = config.action.map((i) => i.key);

  function resolve(keyChar: string, displayLabel: string, overrideAction?: string): MappedKey {
    const labelIdx = labelKeys.indexOf(keyChar);
    if (labelIdx >= 0) {
      return { label: displayLabel, tab: "labeling", actionLabel: config.labeling[labelIdx].action };
    }
    const navIdx = navKeys.indexOf(keyChar);
    if (navIdx >= 0) {
      return { label: displayLabel, tab: "navigation", actionLabel: config.navigation[navIdx].action };
    }
    const actionIdx = actionKeys.indexOf(keyChar);
    if (actionIdx >= 0) {
      return { label: displayLabel, tab: "action", actionLabel: overrideAction ?? config.action[actionIdx].action };
    }
    return { label: displayLabel };
  }

  return [
    // 숫자열
    [
      { label: "`" },
      resolve("1", "1"),
      resolve("2", "2"),
      resolve("3", "3"),
      resolve("4", "4"),
      resolve("5", "5"),
      resolve("6", "6"),
      resolve("7", "7"),
      resolve("8", "8"),
      resolve("9", "9"),
      resolve("0", "0"),
      { label: "-" },
      { label: "=" },
      {
        label: "⌫",
        wider: true,
        ...(actionKeys.includes("Backspace") ? { tab: "action" as TabType, actionLabel: "라벨 초기화" } : {}),
      },
    ],
    // QWERTY열
    [
      { label: "Tab", wide: true },
      resolve("q", "Q"),
      resolve("w", "W"),
      resolve("e", "E"),
      resolve("r", "R"),
      resolve("t", "T"),
      resolve("y", "Y"),
      resolve("u", "U"),
      resolve("i", "I"),
      resolve("o", "O"),
      resolve("p", "P"),
      { label: "[" },
      { label: "]" },
      { label: "\\", wide: true },
    ],
    // ASDF열
    [
      { label: "Caps", wide: true },
      resolve("a", "A", "Ctrl+A"),
      resolve("s", "S", "Ctrl+S"),
      resolve("d", "D"),
      resolve("f", "F"),
      resolve("g", "G"),
      resolve("h", "H"),
      resolve("j", "J"),
      resolve("k", "K"),
      resolve("l", "L"),
      { label: ";" },
      { label: "'" },
      { label: "Enter", wider: true },
    ],
    // ZXCV열
    [
      { label: "Shift", wider: true },
      resolve("z", "Z", "Ctrl+Z"),
      resolve("x", "X"),
      resolve("c", "C"),
      resolve("v", "V"),
      resolve("b", "B"),
      resolve("n", "N"),
      resolve("m", "M"),
      { label: "," },
      { label: "." },
      { label: "/" },
      { label: "Shift", wider: true },
    ],
  ];
}

const BOTTOM_ROW: MappedKey[] = [
  { label: "Ctrl", wide: true },
  { label: "Alt", wide: true },
  { label: "Space", wider: true },
  { label: "Alt", wide: true },
];

function KeyButton({ keyData, onClick }: { keyData: MappedKey; onClick?: (tab: TabType) => void }) {
  const c = keyData.tab ? COLORS[keyData.tab] : COLORS.default;
  const isClickable = !!keyData.tab;

  return (
    <Tooltip title={keyData.actionLabel ?? ""} placement="top" arrow disableHoverListener={!keyData.actionLabel}>
      <Box
        onClick={() => keyData.tab && onClick?.(keyData.tab)}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "7px",
          border: `1px solid ${c.border}`,
          borderBottom: `2px solid ${c.borderBottom}`,
          background: c.bg,
          minWidth: keyData.wider ? 68 : keyData.wide ? 52 : 36,
          height: 36,
          px: "4px",
          cursor: isClickable ? "pointer" : "default",
          transition: "all .12s",
          userSelect: "none",
          flexShrink: 0,
          "&:hover": isClickable
            ? {
                background: c.hoverBg,
                borderColor: c.borderBottom,
                transform: "translateY(-1px)",
              }
            : {},
          "&:active": isClickable ? { transform: "translateY(0px)", borderBottomWidth: "1px" } : {},
        }}
      >
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: c.text, lineHeight: 1, fontFamily: "monospace" }}>
          {keyData.label}
        </Typography>
        {keyData.tab === "labeling" && keyData.actionLabel && (
          <Typography sx={{ fontSize: 9, color: COLORS.labeling.borderBottom, mt: "2px", lineHeight: 1 }}>
            {keyData.actionLabel.replace("라벨 ", "")}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
}

// 화살표 키 (↑↓ 묶음)
function ArrowCluster({ config, onClick }: { config: KeyConfig; onClick: (tab: TabType) => void }) {
  const navKeys = config.navigation.map((i) => i.key);
  const upMapped = navKeys.includes("ArrowUp");
  const downMapped = navKeys.includes("ArrowDown");
  const leftMapped = navKeys.includes("ArrowLeft");
  const rightMapped = navKeys.includes("ArrowRight");

  const arrowBtn = (label: string, mapped: boolean) => (
    <Tooltip
      title={
        mapped
          ? (config.navigation.find(
              (i) =>
                i.key === `Arrow${label === "↑" ? "Up" : label === "↓" ? "Down" : label === "←" ? "Left" : "Right"}`,
            )?.action ?? "")
          : ""
      }
      placement="top"
      arrow
    >
      <Box
        onClick={() => mapped && onClick("navigation")}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 36,
          height: 36,
          borderRadius: "7px",
          border: `1px solid ${mapped ? COLORS.navigation.border : COLORS.default.border}`,
          borderBottom: `2px solid ${mapped ? COLORS.navigation.borderBottom : COLORS.default.borderBottom}`,
          background: mapped ? COLORS.navigation.bg : COLORS.default.bg,
          cursor: mapped ? "pointer" : "default",
          transition: "all .12s",
          flexShrink: 0,
          "&:hover": mapped ? { background: COLORS.navigation.hoverBg, transform: "translateY(-1px)" } : {},
          "&:active": mapped ? { transform: "translateY(0)", borderBottomWidth: "1px" } : {},
        }}
      >
        <Typography
          sx={{ fontSize: 13, fontWeight: 700, color: mapped ? COLORS.navigation.text : COLORS.default.text }}
        >
          {label}
        </Typography>
      </Box>
    </Tooltip>
  );

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
      {arrowBtn("←", leftMapped)}
      <Box sx={{ display: "flex", flexDirection: "column", gap: "3px" }}>
        {arrowBtn("↑", upMapped)}
        {arrowBtn("↓", downMapped)}
      </Box>
      {arrowBtn("→", rightMapped)}
    </Box>
  );
}

interface DetailPanelProps {
  tab: TabType;
  config: KeyConfig;
  onClose: () => void;
  onEdit: (tab: TabType) => void;
}

function DetailPanel({ tab, config, onClose, onEdit }: DetailPanelProps) {
  const items = config[tab];
  const tabLabel = { labeling: "라벨링", navigation: "내비게이션", action: "액션" }[tab];
  const c = COLORS[tab];

  return (
    <Box
      sx={{
        background: "var(--dk-surface, #fff)",
        borderRadius: "12px",
        border: "1px solid var(--dk-border, #e4e4ef)",
        p: "16px 20px",
        mb: "16px",
        animation: "slideDown .18s ease-out",
        "@keyframes slideDown": {
          from: { opacity: 0, transform: "translateY(-6px)" },
          to: { opacity: 1, transform: "none" },
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "12px" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Box sx={{ width: 8, height: 8, borderRadius: "2px", background: c.borderBottom }} />
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "var(--dk-text, #1a1a2e)" }}>
            {tabLabel} 키 목록
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <Button
            size="small"
            onClick={() => onEdit(tab)}
            sx={{
              fontSize: 11,
              px: "10px",
              py: "4px",
              borderRadius: "6px",
              border: `1px solid ${c.border}`,
              background: c.bg,
              color: c.text,
              textTransform: "none",
              minWidth: 0,
              "&:hover": { background: c.hoverBg },
            }}
          >
            편집
          </Button>
          <IconButton
            size="small"
            onClick={onClose}
            sx={{
              width: 24,
              height: 24,
              borderRadius: "5px",
              border: "1px solid var(--dk-border, #e4e4ef)",
              color: "var(--dk-text-3, #9090a8)",
              "&:hover": { background: "#f5f5f8", color: "var(--dk-text-2, #3a3a5c)" },
            }}
          >
            <CloseOutlined sx={{ fontSize: 13 }} />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
        {items.map((item) => (
          <Box
            key={item.id}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: "10px",
              py: "7px",
              borderRadius: "7px",
              borderBottom: "0.5px solid #f0f0f8",
              "&:last-child": { borderBottom: "none" },
            }}
          >
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 500, color: "var(--dk-text, #2a2a4a)" }}>
                {item.action}
              </Typography>
              <Typography sx={{ fontSize: 10, color: "var(--dk-text-3, #9090a8)" }}>{item.desc}</Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "3px",
                px: "8px",
                py: "5px",
                borderRadius: "6px",
                border: `1px solid ${c.border}`,
                background: c.bg,
                borderBottom: `2px solid ${c.borderBottom}`,
              }}
            >
              {item.modifier && (
                <>
                  <Typography sx={{ fontSize: 10, fontWeight: 700, color: c.text, fontFamily: "monospace" }}>
                    {item.modifier}
                  </Typography>
                  <Typography sx={{ fontSize: 10, color: "var(--dk-text-3, #b0b0c8)" }}>+</Typography>
                </>
              )}
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: c.text, fontFamily: "monospace" }}>
                {item.display ?? item.key.toUpperCase()}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export function KeyMappingPanel() {
  const projectConfig = useRecoilValue(projectConfigState);
  const configStr = projectConfig?.keyConfig
    ? typeof projectConfig.keyConfig === "string"
      ? projectConfig.keyConfig
      : JSON.stringify(projectConfig.keyConfig)
    : undefined;

  console.log("projectConfig", projectConfig);
  const { config } = useKeyMapping(configStr);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalInitialTab, setModalInitialTab] = useState<TabType>("labeling");
  const [detailTab, setDetailTab] = useState<TabType | null>(null);

  const layout = buildLayout(config);

  const handleKeyClick = useCallback((tab: TabType) => {
    setDetailTab((prev) => (prev === tab ? null : tab));
  }, []);

  const openModal = useCallback((tab: TabType) => {
    setDetailTab(null);
    setModalInitialTab(tab);
    setModalOpen(true);
  }, []);

  return (
    <Box sx={{ p: "24px", overflowY: "auto", maxHeight: "calc(100vh - 200px)" }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: "16px" }}>
        <Box>
          <Typography sx={{ fontSize: 15, fontWeight: 600, color: "var(--dk-text, #1a1a2e)" }}>키 매핑 현황</Typography>
          <Typography sx={{ fontSize: 12, color: "var(--dk-text-3, #9090a8)", mt: "2px" }}>
            매핑된 키를 클릭하면 상세 정보를 확인할 수 있어요
          </Typography>
        </Box>
        <Button
          size="small"
          startIcon={<EditOutlined sx={{ fontSize: 13 }} />}
          onClick={() => {
            setModalInitialTab("labeling");
            setModalOpen(true);
          }}
          sx={{
            fontSize: 12,
            px: "12px",
            py: "6px",
            borderRadius: "8px",
            border: "1px solid #d0d0e0",
            background: "var(--dk-surface, #fff)",
            color: "#5a5a7a",
            textTransform: "none",
            fontWeight: 500,
            "&:hover": { borderColor: "#7c6cf0", color: "#7c6cf0", background: "#f5f3ff" },
          }}
        >
          키 매핑 편집
        </Button>
      </Box>

      {/* 범례 */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "14px", mb: "14px", flexWrap: "wrap" }}>
        {(["labeling", "navigation", "action", "default"] as const).map((type) => (
          <Box key={type} sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <Box sx={{ width: 8, height: 8, borderRadius: "2px", background: COLORS[type].dot }} />
            <Typography sx={{ fontSize: 11, color: "#8888a0" }}>
              {{ labeling: "라벨링", navigation: "내비게이션", action: "액션", default: "미할당" }[type]}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* 키보드 */}
      <Box
        sx={{
          background: "var(--dk-surface, #fff)",
          borderRadius: "14px",
          border: "1px solid var(--dk-border, #e4e4ef)",
          p: "20px",
          mb: "16px",
        }}
      >
        <Typography
          sx={{ fontSize: 11, fontWeight: 500, color: "var(--dk-text-3, #9090a8)", mb: "14px", letterSpacing: ".06em" }}
        >
          키보드 매핑 현황 — 색상으로 구분된 키를 클릭하여 확인
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: "5px", overflowX: "auto", pb: "4px" }}>
          {layout.map((row, ri) => (
            <Box key={ri} sx={{ display: "flex", gap: "5px", alignItems: "center" }}>
              {row.map((key, ki) => (
                <KeyButton key={ki} keyData={key} onClick={handleKeyClick} />
              ))}
            </Box>
          ))}

          {/* 스페이스 + 화살표 행 */}
          <Box sx={{ display: "flex", gap: "5px", alignItems: "center" }}>
            {BOTTOM_ROW.map((key, ki) => (
              <KeyButton key={ki} keyData={key} />
            ))}
            <Box sx={{ flex: 1 }} />
            <ArrowCluster config={config} onClick={handleKeyClick} />
          </Box>
        </Box>
      </Box>

      {/* 상세 패널 */}
      {detailTab && (
        <DetailPanel tab={detailTab} config={config} onClose={() => setDetailTab(null)} onEdit={openModal} />
      )}

      {/* 편집 모달 */}
      <KeyMappingModal open={modalOpen} onClose={() => setModalOpen(false)} initialTab={modalInitialTab} />
    </Box>
  );
}
